import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../../lib/api/client'
import { SPREADSHEET_UNSUPPORTED_MESSAGE, isSpreadsheetWorkbook } from '../../lib/import-file-type'
import { assertWithinMaxBytes, putFileToS3 } from './s3Upload'
import type { S3PutTicket } from './s3Upload'
import { generateAndUploadThumbnail } from './thumbnail/generateThumbnail'
import type {
  CreatedUploadSession,
  PresignUploadRequest,
  PresignUploadTicket,
  UploadFileState,
  UploadSessionPutTicket,
  UploadSessionSummary,
} from './types'

/** Pull the `title` off a Problem+JSON error body for a user-facing message. */
function problemTitle(error: unknown, fallback: string): string {
  return typeof error === 'object' && error !== null && 'title' in error
    ? String((error as { title?: unknown }).title)
    : fallback
}

/**
 * ADR-0135 D5a — prefer the Problem `detail` over its `title`, falling back to
 * `title` and then the generic message.
 *
 * Session-first (D1) makes `createSession` the FIRST network call, and its
 * validation is all-or-nothing, so one off-allowlist or oversize file now fails
 * the whole batch (415/413) instead of just its own row. That is a real,
 * accepted regression in per-file attribution (residual R7 is the real fix, a
 * breaking response-semantics change). The mitigation is to name the offending
 * file: the server already sends `${filename}: ${reason}` as `detail`
 * (`uploads-routes.ts`), and the old generic "We couldn't start a staging
 * session." threw that away. Deliberately NOT mitigated by mirroring the server
 * allowlist client-side — two sources of truth for one policy will drift, which
 * is a worse defect than the symptom (D5c).
 */
function problemMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'detail' in error) {
    const detail = (error as { detail?: unknown }).detail
    if (typeof detail === 'string' && detail.trim() !== '') return detail
  }
  return problemTitle(error, fallback)
}

/** `CreateUploadSessionRequest.name`'s contract ceiling
 * (`apps/api/openapi/schemas/intake.yaml`, `maxLength: 200`). */
const SESSION_NAME_MAX_LENGTH = 200

/**
 * The "Review upload" session title (staging page heading chip,
 * `StagingPage.tsx`; the uploads-list session rows,
 * `InProgressSessionCard.tsx`/`ConfirmedSessionRow.tsx`) is `session.name`,
 * which the client never used to send — `CreateUploadSessionRequest.name`
 * is OPTIONAL and, when absent, the server falls back to a generic
 * date-stamped default (`defaultSessionName`, `uploads-routes.ts`), e.g.
 * "CAD upload 2026-09-07". This derives a real title from the first file in
 * the batch instead — literally the filename with its extension stripped,
 * matching the design's own `stem()` (`Fictiv Platform.dc.html`'s
 * `beginAnalysis`: `f.name.replace(/\.[^.]+$/,'')`). No further
 * beautification (title-casing, underscore-to-space) — the ask was the
 * filename minus extension, not a prettified version of it.
 *
 * Clamped to the contract's own `maxLength: 200` — an unclamped name over
 * that ceiling would 400 the WHOLE `createSession` call over a cosmetic
 * title, failing an upload that otherwise fully succeeded.
 */
function stripFileExtension(filename: string): string {
  return filename.replace(/\.[^./\\]+$/, '').slice(0, SESSION_NAME_MAX_LENGTH)
}

/**
 * The data-layer seam for Uploads (UID-41/UID-140). REAL end to end
 * (UID-106): `presignUpload` (UID-40, `POST /intake/uploads`),
 * `listSessions` (`GET /uploads/sessions`), and `createSession` (`POST
 * /uploads/sessions`) all go through the one generated `apiClient` — no
 * mock-data.
 *
 * `deleteSession` (UID-172) is now REAL too: `DELETE
 * /uploads/sessions/{sessionId}` through the same generated `apiClient`.
 * `seedSampleSession`/`confirmSession` (UID-140) remain typed, real seam
 * members whose `defaultUploadsService` implementations are thin TODO stubs —
 * see each method below and its TODO(UID-141/UID-146) marker — because the BFF
 * has no confirm/seed-sample endpoint yet. The injectable test/demo service
 * (`test-fixtures.ts`'s `mockUploadsService`) implements all of them against
 * in-memory fixtures so the full S4/S5/S6 design can be exercised in tests
 * without lying about what production does today.
 */
export interface UploadsService {
  listSessions(): Promise<readonly UploadSessionSummary[]>
  /**
   * @deprecated ADR-0135 D1/D3a — DEAD ON THE BATCH UPLOAD PATH. The object key has
   * exactly ONE minter and it is `createSession`, which persists the key in the same
   * breath as signing it. This second, independent presign is what made ADR-0134's
   * capture seam inert (R5): `useUploadFlow` used to call `createSession`, throw away
   * its `files[]`, presign every file AGAIN here, and PUT the bytes to a different
   * key the server had never stored. `useUploadFlow` no longer calls this at all —
   * `useUploadFlow.session-first.test.tsx` asserts `presignUpload` is NEVER invoked.
   *
   * Kept on the interface, not deleted, because `POST /intake/uploads` is a real
   * FROZEN single-file BFF endpoint (UID-40/UID-105) with its own contract and its
   * own audit record, and removing the seam member would be a wider change than this
   * fix. DO NOT call it from a batch/session flow: reintroducing a second minter
   * reintroduces R5. Use `createSession` and PUT to the `objectKey` it returns.
   */
  presignUpload(request: PresignUploadRequest): Promise<PresignUploadTicket>
  /** Creates a batch staging session for the given files (filename +
   * content-type + size, same shape `presignUpload` takes per-file) — the
   * BFF mints one presigned ticket per file server-side; the caller
   * navigates to `/uploads/:sessionId` (UID-42) with the returned summary.
   *
   * ADR-0135 D1 — this is the batch flow's ONLY presign, and it resolves with
   * the `tickets` the browser PUTs to (INDEX-ALIGNED with `files`, D4). It used
   * to map the response down to a summary and DROP `files[]`, so the browser
   * presigned every file again via `POST /intake/uploads` and PUT the bytes to a
   * second, different object key — one the server never persisted. That is what
   * made ADR-0134's whole capture seam inert (R5). The key has exactly ONE
   * minter (D3a) and it is this operation, which persists in the same breath.
   *
   * Rejects with the Problem `detail` when present (D5a), which names the
   * offending file on an all-or-nothing 415/413.
   * `workspaceId` (UID-140, S3's "Uploading to" selector) is OPTIONAL and,
   * on `defaultUploadsService`, not yet sent to the BFF — see the method's
   * TODO(UID-141) marker; passing it is still fully wired end-to-end on the
   * FE side (`useUploadFlow` → here) so only the backend leg is pending. */
  createSession(
    files: readonly PresignUploadRequest[],
    workspaceId?: string,
  ): Promise<CreatedUploadSession>
  /** Seeds a sample/demo staging session so a visitor can explore the
   * staging flow without uploading real files (S6, "Analyze a sample
   * fixture set"). TODO(UID-146): back this with a real BFF endpoint. */
  seedSampleSession(): Promise<UploadSessionSummary>
  /** Confirms a `'ready'` session into the parts library (S4's "Confirm"
   * action). TODO(UID-141): back this with a real BFF endpoint. */
  confirmSession(sessionId: string): Promise<UploadSessionSummary>
  /** Deletes an in-progress upload session (S4's trash action). REAL end to
   * end (UID-172): `DELETE /uploads/sessions/{sessionId}` on the generated
   * `apiClient`. Rejects with a user-facing message on 401/403/404. */
  deleteSession(sessionId: string): Promise<void>
  /** UID-330 — mint a presigned PUT for a client-rendered thumbnail PNG
   * (`POST /uploads/thumbnails/presign`). The thumbnail is ID-ADDRESSED: its S3
   * key is derived server-side as `uploads/thumbnails/{accountId}/{id}.png` (no DB row),
   * where `id` is the stable entity UUID (uploadId/partId/sourceableId). Returns a
   * keyless {@link S3PutTicket} the caller replays via `putFileToS3`.
   *
   * `sessionId` is the OWNERSHIP proof (security review F1, 2026-09-04): the BFF
   * verifies `id` is one of `sessionId`'s own file ids before minting the PUT, so
   * a same-tenant caller cannot presign a write for an id it has no claim to. */
  presignThumbnail(id: string, sessionId: string, sizeBytes: number): Promise<S3PutTicket>
  /**
   * UID-517 follow-up — the PRE-RENDER check `useUploadFlow` runs BEFORE its
   * per-file `generateAndUploadThumbnail` fan-out: "does any of these
   * candidate names already have a real Library photo I can reuse?" One
   * candidate per stageable file (its own base name) — `POST
   * /uploads/sessions/{sessionId}/thumbnail-reuse-check`. Returns the
   * matched subset, name → real thumbnail URL; an unmatched name is simply
   * absent. Best-effort on the caller: a rejection here must degrade to "no
   * reuse offered" (every file renders as it always has), never block or
   * fail the upload — see `useUploadFlow`'s own try/catch around the call.
   *
   * OPTIONAL on the interface (same posture as `updateSessionWorkspace`
   * above, for the same reason): this vertical's other, pre-existing
   * `UploadsService` test doubles don't all need a same-PR update just
   * because the upload flow grew a new pre-render call. Absent means the
   * flow's own fail-open default applies — every file renders exactly as it
   * did before this feature existed.
   */
  checkThumbnailReuse?(
    sessionId: string,
    names: readonly string[],
  ): Promise<Readonly<Record<string, string>>>
  /**
   * ADR-0134 D1 — tell the BFF the session's direct-to-S3 PUTs have landed, so it
   * reads back the BOM spreadsheets and captures their assembly tree. REAL end to
   * end: `POST /uploads/sessions/{sessionId}/complete`.
   *
   * Resolves with nothing on purpose. The response carries a per-file outcome
   * report, but there is no UI for it yet (ADR-0086 D1's contract is frozen and
   * the hierarchy surfaces through `getStagingTriage`, not here), and inventing
   * one would be scope this ADR explicitly does not take. Rejects on a transport
   * or Problem+JSON failure so the CALLER can decide — and the caller's decision
   * is to degrade silently, because the upload itself already succeeded.
   */
  completeUploadSession(sessionId: string): Promise<void>
  /** Assigns or clears an ALREADY-CREATED session's project (UID-425, Bug
   * 1). REAL end to end: `PATCH /uploads/sessions/{sessionId}`
   * (`UpdateUploadSessionRequest`). `workspaceId` is REQUIRED and NULLABLE
   * on the wire — pass `null` to explicitly clear/keep-unfiled (never omit
   * it; omission isn't a valid request body here, unlike `createSession`'s
   * workspaceId). Exists because the design assigns a project in a modal
   * shown AFTER the session already exists (`Fictiv Platform.dc.html
   * :6140`'s `assignModal`/`sessionWs` sequencing) — `createSession`'s
   * `workspaceId` could only ever be set at create time. Rejects with a
   * user-facing Problem+JSON title on failure, including the 404-no-leak
   * conflation of "no such session" and "no such workspace you're a member
   * of" (ADR-0107 / ADR-0114 D6) — callers must not try to distinguish
   * the two causes.
   *
   * OPTIONAL on the interface (unlike every other method here) so the
   * vertical's other, pre-existing `UploadsService` implementations/test
   * doubles (`test-fixtures.ts`'s `mockUploadsService`, and the local
   * fixtures `UploadsPage.test.tsx` builds via `Partial<UploadsService>`
   * overrides) don't all need a same-PR update just because HomePage grew a
   * new call. `defaultUploadsService` below still always provides it (typed
   * via `satisfies`, not a widening annotation, so ITS callers — this
   * vertical's own tests — see the real, non-optional function). A caller
   * that receives an `UploadsService` without it (`HomePage`'s injected
   * `service` prop) must guard before calling. */
  updateSessionWorkspace?(
    sessionId: string,
    workspaceId: string | null,
  ): Promise<UploadSessionSummary>
}

/**
 * The completion call's rejection, carrying the two facts the caller needs to
 * decide whether to retry (Security/code review S5).
 *
 * `completeUploadSession` used to reject with a bare `new Error(title)`, which
 * discarded both the HTTP status and the `Retry-After` header. That mattered
 * because the per-account completion gate answers a second concurrent completion
 * with **429 + `Retry-After: 1`** and the endpoint is idempotent — so the 429 is
 * fully recoverable, while the far more common 503/missing-grant failure is not.
 * With both flattened into one opaque `Error` the caller could not tell them
 * apart, so it retried neither and reported neither: two sessions in one account
 * silently lost the second capture, permanently (there is no polling seam on
 * `StagingPage` to recover it later — ADR-0134 R2).
 */
export class CompleteUploadSessionError extends Error {
  constructor(
    message: string,
    /** HTTP status, or `null` for a transport failure with no response. */
    readonly status: number | null,
    /** Parsed `Retry-After` in SECONDS, or `null` when absent/unparseable. */
    readonly retryAfterSeconds: number | null,
  ) {
    super(message)
    this.name = 'CompleteUploadSessionError'
  }
}

/**
 * `Retry-After` in delta-seconds. The BFF sends `1`; the HTTP-date form is
 * deliberately NOT parsed — nothing we control emits it, and guessing at a clock
 * skew would be worse than falling back to the default back-off.
 */
function parseRetryAfterSeconds(value: string | null | undefined): number | null {
  if (value == null) {
    return null
  }
  const seconds = Number(value.trim())
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null
}

export const defaultUploadsService = {
  async listSessions() {
    const { data, error } = await apiClient.GET('/uploads/sessions')
    if (error || !data) {
      throw new Error(problemTitle(error, 'We couldn’t load recent uploads.'))
    }
    return data.sessions
  },
  async presignUpload(request) {
    const { data, error } = await apiClient.POST('/intake/uploads', { body: request })
    if (error || !data) {
      throw new Error(problemTitle(error, 'Upload could not be started'))
    }
    return data
  },
  async createSession(files: readonly PresignUploadRequest[], workspaceId?: string) {
    // UID-298 (Slice B of UID-258): `CreateUploadSessionRequest` now carries
    // `workspaceId`, persisted server-side (`intake.staging_session.
    // workspace_id`, migration 1010) and echoed back on the response — real
    // end to end, no longer a discarded/fabricated field. `workspaceName`
    // stays undefined (the BFF doesn't return a display name); the caller
    // resolves it from the SAME workspaces list `WorkspaceSelector` already
    // fetched, if it needs one.
    const firstFile = files[0]
    const { data, error } = await apiClient.POST('/uploads/sessions', {
      body: {
        // See `stripFileExtension`'s doc comment — sends a real,
        // filename-derived title instead of leaving the server to fall back
        // to its generic date-stamped default.
        ...(firstFile ? { name: stripFileExtension(firstFile.filename) } : {}),
        files: files.map((file) => ({
          filename: file.filename,
          contentType: file.contentType,
          sizeBytes: file.sizeBytes,
        })),
        ...(workspaceId !== undefined ? { workspaceId } : {}),
      },
    })
    if (error || !data) {
      // D5a: `detail` names the file the server rejected; `title` alone does not.
      throw new Error(problemMessage(error, 'We couldn’t start a staging session.'))
    }
    return {
      sessionId: data.sessionId,
      name: data.name,
      fileCount: data.files.length,
      status: data.status,
      createdAt: data.createdAt,
      ...(data.workspaceId != null ? { workspaceId: data.workspaceId } : {}),
      // ADR-0135 D1 — the tickets, no longer discarded. Order is preserved
      // VERBATIM: `data.files[i]` is the ticket for the `files[i]` we posted
      // (D4), so no sort, no de-dupe, no grouping may be introduced here.
      // D4a: compose the SESSION-level `maxBytes` onto each ticket rather than
      // adding a per-file contract field, so these satisfy `s3Upload.ts`'s
      // narrow structural `S3PutTicket` with no change to that module.
      tickets: data.files.map(
        (ticket): UploadSessionPutTicket => ({
          uploadId: ticket.uploadId,
          filename: ticket.filename,
          objectKey: ticket.objectKey,
          url: ticket.url,
          method: ticket.method,
          headers: ticket.headers,
          expiresAt: ticket.expiresAt,
          maxBytes: data.maxBytes,
        }),
      ),
    }
  },
  async seedSampleSession() {
    // TODO(UID-146): no real "seed a sample fixture set" endpoint exists
    // yet. Surfacing a clear, catchable error (rather than silently
    // fabricating a fake session) so the UI can show an honest message
    // instead of pretending sample data was created server-side.
    throw new Error('Sample fixture sets aren’t available yet — this is coming in UID-146.')
  },
  async confirmSession(_sessionId) {
    // TODO(UID-141): intended shape once shipped —
    // `POST /uploads/sessions/{sessionId}/confirm` -> `UploadSession`.
    throw new Error('Confirming uploads isn’t wired to the backend yet (UID-141).')
  },
  async completeUploadSession(sessionId) {
    // ADR-0134 D1. A 200 carries the per-file outcome report, which we
    // deliberately do not consume (see the interface doc); only `error` gates
    // success, and the caller treats even that as non-fatal.
    const { error, response } = await apiClient.POST('/uploads/sessions/{sessionId}/complete', {
      params: { path: { sessionId } },
    })
    if (error) {
      // S5 — keep the status and `Retry-After`. `response` is optional-by-shape
      // (a transport failure surfaces no response at all), so both reads are
      // guarded: a crash while reading the error would turn a recoverable degrade
      // into a broken upload flow.
      throw new CompleteUploadSessionError(
        problemTitle(error, 'We couldn’t read the uploaded BOM spreadsheet.'),
        response?.status ?? null,
        parseRetryAfterSeconds(response?.headers?.get?.('Retry-After')),
      )
    }
  },
  async deleteSession(sessionId) {
    // UID-172: real DELETE /uploads/sessions/{sessionId}. A 2xx (204) leaves
    // `error` undefined; any Problem+JSON (401/403/404) surfaces its title so
    // the ConfirmDialog can show an honest message. 204 has no body, so there
    // is no `data` to inspect — only `error` gates success.
    const { error } = await apiClient.DELETE('/uploads/sessions/{sessionId}', {
      params: { path: { sessionId } },
    })
    if (error) {
      throw new Error(problemTitle(error, 'We couldn’t delete this upload.'))
    }
  },
  async presignThumbnail(id, sessionId, sizeBytes) {
    const { data, error } = await apiClient.POST('/uploads/thumbnails/presign', {
      body: { id, sessionId, sizeBytes },
    })
    if (error || !data) {
      throw new Error(problemTitle(error, 'Thumbnail upload could not be started'))
    }
    return data
  },
  async checkThumbnailReuse(sessionId, names) {
    const { data, error } = await apiClient.POST(
      '/uploads/sessions/{sessionId}/thumbnail-reuse-check',
      { params: { path: { sessionId } }, body: { names: [...names] } },
    )
    if (error || !data) {
      throw new Error(problemTitle(error, 'We couldn’t check for a reusable thumbnail'))
    }
    return data.matches
  },
  async updateSessionWorkspace(sessionId, workspaceId) {
    // UID-425 (Bug 1): real PATCH /uploads/sessions/{sessionId}. `workspaceId`
    // is sent verbatim (including `null`) — the wire contract requires the
    // key present and distinguishes `null` (clear) from omitted (invalid).
    const { data, error } = await apiClient.PATCH('/uploads/sessions/{sessionId}', {
      params: { path: { sessionId } },
      body: { workspaceId },
    })
    if (error || !data) {
      throw new Error(problemTitle(error, 'We couldn’t file these files under that project.'))
    }
    return data
  },
} satisfies UploadsService

export type UploadSessionsState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly sessions: readonly UploadSessionSummary[] }

export function useUploadSessions(service: UploadsService = defaultUploadsService) {
  const [state, setState] = useState<UploadSessionsState>({ status: 'loading' })
  const [generation, setGeneration] = useState(0)
  // UID-335 — distinguishes the initial load / a `service` swap (must show
  // loading) from a same-service background refetch (`refetch()`'s
  // generation bump, incl. the automatic focus/delay ones the Uploads page
  // wires in): only the former resets `state` to `loading`. A silent
  // background refetch keeps showing the current sessions until the new
  // fetch resolves, so a background thumbnail catching up never flashes the
  // whole list back to its loading skeleton.
  const prevServiceRef = useRef<UploadsService | null>(null)

  // `generation` is a manual re-fetch trigger (bumped by `refetch`), not
  // read in the effect body.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    let cancelled = false
    const serviceChanged = prevServiceRef.current !== service
    prevServiceRef.current = service
    if (serviceChanged) {
      setState({ status: 'loading' })
    }
    service
      .listSessions()
      .then((sessions) => {
        // UID-330 — `thumbnailUrls` now arrive INLINE on each session summary
        // (populated server-side by `GET /uploads/sessions`), so the card renders
        // its real thumbnails directly with no separate fetch or enrichment pass.
        if (!cancelled) setState({ status: 'ready', sessions })
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          // A background refetch that fails keeps showing the last good data
          // rather than clobbering it with an error the user never asked for.
          setState((prev) =>
            !serviceChanged && prev.status === 'ready'
              ? prev
              : {
                  status: 'error',
                  message:
                    error instanceof Error ? error.message : 'We couldn’t load recent uploads.',
                },
          )
        }
      })

    return () => {
      cancelled = true
    }
  }, [service, generation])

  const refetch = useCallback(() => setGeneration((g) => g + 1), [])

  return { state, refetch }
}

function makeUploadFileId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/**
 * UID-583 (ADR-0173 D2) — where an upload batch has got to, for the ONE
 * consumer that cannot see the flow's own hook state: `StagingPage`, which
 * the flow now navigates to while the bytes are still moving.
 *
 *   - `'uploading'` — presigned PUTs in flight (ADR-0135 D7 starts them all
 *     at once).
 *   - `'completing'` — every PUT reached a terminal state; the BOM-hierarchy
 *     capture call is in flight (bounded by
 *     {@link COMPLETE_SESSION_TIMEOUT_MS}).
 *   - `'done'` — the flow is finished with this session. The triage read may
 *     happen. This is reached even when the capture call timed out or failed,
 *     because `completeSessionBestEffort` never throws and a late capture is
 *     picked up by the next triage read anyway (ADR-0134's own reasoning).
 *   - `'failed'` — NOT ONE FILE uploaded. The session exists but is empty, so
 *     there is nothing for the triage to show; the consumer must render the
 *     error instead of waiting forever. ADR-0173 D5.
 */
export type UploadBatchPhase = 'uploading' | 'completing' | 'done' | 'failed'

export interface UploadBatchState {
  readonly sessionId: string
  readonly phase: UploadBatchPhase
  /** Byte-weighted, 0-100 — a REAL measurement (see
   * {@link byteWeightedProgress}), unlike the analysis ramp that follows it. */
  readonly pct: number
  /** Files that reached a TERMINAL state (uploaded OR failed), so it reaches
   * `total` exactly when the batch stops being in flight. */
  readonly settledCount: number
  readonly total: number
  /**
   * Per-file TRANSPORT failures, in batch order: the bytes were sent and did
   * not land. ADR-0173 D5 — the triage cannot report these (`filesToResolve`
   * is built from `staged_file` rows without head-objecting S3), so this is
   * the only carrier once the upload manifest is gone.
   *
   * Carries `id` (the row id), NOT just the filename, because filenames are
   * NOT unique within a batch — two dragged folders can each contain
   * `bom.csv`. That is the same reason ADR-0135 D4 correlates tickets by
   * index and never by name, and a consumer keying a list on `fileName`
   * would collide on exactly that input.
   */
  readonly failedFiles: readonly {
    readonly id: string
    readonly fileName: string
    readonly error: string
  }[]
  /**
   * Files REFUSED before any byte moved — today only the spreadsheet-workbook
   * gate (ADR-0134 D7), which seeds them terminal-`'failed'` and never
   * presigns them.
   *
   * A SEPARATE list from {@link failedFiles}, not merged into it, because the
   * two have different remedies and merging them would attach the wrong one
   * to half the rows: a refused workbook needs "save it as CSV" (retrying the
   * identical file fails identically), while a transport failure needs "try
   * again". They are also excluded from `total`/`settledCount` on purpose —
   * those describe the transfer, and a file that was never sent would
   * otherwise report as "uploaded" the instant the batch started.
   *
   * ADDED AFTER Gate-5 finding C1: `fileIds` is built from the STAGEABLE
   * files, so before this list existed these rows appeared in no batch state
   * at all and the staging screen named them nowhere — silently dropping the
   * one message that tells the user what to do about an `.xlsx`.
   */
  readonly refusedFiles: readonly {
    readonly id: string
    readonly fileName: string
    readonly error: string
  }[]
  /** Set only when the whole batch failed (`phase === 'failed'`). */
  readonly batchError: string | null
}

/**
 * Byte-weighted batch progress — NOT a mean of per-file percentages. A 200MB
 * file sitting at 10% and a 20KB file that finished at 100% should report a
 * number close to 10%: bytes transferred / bytes total, not percentages
 * averaged. `sizeBytes`/`progress` are the real, measurable inputs
 * `putFileToS3` (`s3Upload.ts`) already streams into `updateFile`, so this is
 * a real aggregate, not a fabricated one.
 *
 * UID-583 — moved here from `UploadFileList.tsx` (UID-550), which this story
 * deletes. The measurement outlives the panel it was built for: it now feeds
 * `StagingPage`'s analysing bar for the upload phase (ADR-0173 D6).
 */
export function byteWeightedProgress(files: readonly UploadFileState[]): number {
  const totalBytes = files.reduce((sum, file) => sum + file.sizeBytes, 0)
  if (totalBytes === 0) return 0
  const transferredBytes = files.reduce(
    (sum, file) => sum + file.sizeBytes * (file.progress / 100),
    0,
  )
  return (transferredBytes / totalBytes) * 100
}

export interface UseUploadFlowOptions {
  /**
   * Announce a NON-BLOCKING notice about the batch (Security/code review S5) —
   * either {@link BOM_CAPTURE_BUSY_NOTICE}, or the client-side "N parts parsed
   * from …" count ({@link countBomCsvDataRows}) for a BOM-spreadsheet upload.
   * Every call site passes `useToast().showToast`.
   *
   * A CALLBACK RATHER THAN HOOK STATE, and that is the whole point. The flow
   * NAVIGATES immediately after (deliberately — the upload succeeded, so nothing
   * here may block the user), which unmounts the page that owns this hook. Notice
   * state on the result would therefore be set and then thrown away in the same
   * commit: rendered nowhere, seen by nobody, and functionally identical to the
   * bare `catch {}` this replaces. The toast host lives ABOVE `<Routes>` in
   * `App.tsx`, so a notice raised here survives the navigation, is `aria-live=
   * "polite"`, and self-dismisses.
   *
   * Optional so the many existing tests that render this hook without a toast
   * host keep working unchanged.
   *
   * `position` defaults to the shared stack's bottom-center — omit it
   * (as {@link BOM_CAPTURE_BUSY_NOTICE} does) for that. Only the "N parts
   * parsed" notice below passes `'top'`, to sit above the "Add these files
   * to a project" modal that opens alongside it, matching the design.
   */
  readonly onNotice?: (message: string, position?: 'top' | 'bottom') => void
}

export interface UseUploadFlowResult {
  readonly files: readonly UploadFileState[]
  readonly batchError: string | null
  /** `workspaceId` (UID-140, S3) is optional — omit it (or pass `undefined`)
   * to keep the session a draft/unfiled, matching every existing call site
   * (Home has no workspace selector) without changing their behavior.
   *
   * `onSessionCreated` (UID-425, Bug 1) is an OPTIONAL third param — every
   * existing call site (`UploadsPage`) omits it and is unaffected. When
   * given, it fires AFTER `navigate` (UID-509 — the session already
   * durably exists and navigation is not gated on it), giving the caller a
   * chance to do something (show a project-assignment modal, apply a PATCH)
   * on the destination page. This is `AssignToProjectProvider`'s seam for
   * the home-hero dropzone's "Add these files to a project" modal. */
  readonly handleFilesSelected: (
    selected: readonly File[],
    workspaceId?: string,
    onSessionCreated?: (session: UploadSessionSummary) => Promise<void> | void,
  ) => Promise<void>
  /**
   * UID-583 (ADR-0173 D3) — where `sessionId`'s batch has got to, or `null`
   * if this flow instance did not start it.
   *
   * `null` is the NORMAL answer for every arrival at a staging session that
   * this hook did not just upload (a resumed session card, a bookmarked or
   * shared URL, a `refetch()`), and consumers MUST fall through to their
   * pre-UID-583 behaviour when they get it — see `useStaging`'s
   * `minLoadingMs` floor, which is still what covers those paths. Only the
   * most recent batch is tracked, deliberately: the flow is only reachable
   * from a dropzone, and a second batch means the first one's staging page
   * is no longer the screen in front of the user.
   */
  readonly batchFor: (sessionId: string) => UploadBatchState | null
  /** UID-583 — drops a batch error from an earlier selection. Needed because
   * this hook now lives in `UploadFlowProvider`, which outlives every page
   * that shows the error (see that file's `clearBatchError` doc). */
  readonly clearBatchError: () => void
}

/**
 * ADR-0134 Step 6 — signal completion, then get out of the way.
 *
 * AWAITED, not fire-and-forget, and that is the whole design. `useStaging` fires
 * exactly ONE `getStagingTriage` per session; there is no polling seam and
 * `analysisProgress` is hardcoded to 100 (residual R2). So the hierarchy has to
 * exist BEFORE that read happens, or the user sits in front of the ADR-0079 flat
 * table with nothing in the UI able to tell them a tree is coming.
 *
 * UID-583 (ADR-0173 D3) — WHAT CHANGED, since this block is the ADR-0134 D2
 * rationale and used to say "before the user REACHES `StagingPage`". The user now
 * reaches it first: ADR-0173 D1 navigates as soon as the session exists, and this
 * call runs while they are already watching the "Analyzing your files" panel. The
 * ORDERING CONSTRAINT IS UNCHANGED AND STILL ABSOLUTE — it is simply enforced
 * somewhere else. `StagingPage` passes `waitForUpload` to `useStaging`, which
 * holds the one `getStagingTriage` until this call has settled, so "the hierarchy
 * exists before the triage read" still holds. What is gone is only the claim that
 * the user cannot be on that route yet.
 *
 * The wait is bounded on BOTH sides. Server-side: at most 4 spreadsheets of ≤5 MB,
 * parsed sequentially in a single linear pass — measured (Gate 5 F1) at 286–392 ms
 * for one COMPLETING 5 MB / 10,000-row file and 1144–1199 ms for the four-attempt
 * worst case, not the "~100–500 ms" previously claimed here. Client-side: a hard
 * {@link COMPLETE_SESSION_TIMEOUT_MS} race, so a stalled socket or a 429 back-off
 * cannot leave the user waiting indefinitely with no affordance (Gate 5 W5) —
 * post-UID-583 that ceiling releases the held triage read rather than a blocked
 * navigation, and the affordance during it is the analysing panel that ADR-0134's
 * own note on {@link COMPLETE_SESSION_TIMEOUT_MS} called "the real fix". ADR-0134
 * D2 records paying the blocking wait instead of building a progress subsystem as
 * a deliberate trade-off; that trade-off still stands.
 *
 * NEVER RETHROWS. The bytes are already in S3 and the flat staging view works, so
 * a failure here means "no hierarchy this time", not "your upload failed".
 * Surfacing it as a batch error would both block the user from work that DID
 * succeed and tell them something untrue. This is not hypothetical: until the BFF
 * task role gains `s3:GetObject` + `kms:Decrypt` on the uploads bucket (a
 * CloudFormation change owned by devops, tracked outside this repo), this call
 * fails closed in every deployed environment — and the upload flow must keep
 * working exactly as it does today throughout.
 *
 * "NEVER RETHROWS" IS NOT "NEVER LOOKS" (Security/code review S5). This used to be
 * a bare `catch {}` with no retry, which made two failures with opposite remedies
 * indistinguishable. The per-account completion gate's **429 + `Retry-After: 1`**
 * is self-resolving and the endpoint is idempotent, so it is retried — bounded by
 * {@link MAX_COMPLETE_RETRIES} and always inside the SAME
 * {@link COMPLETE_SESSION_TIMEOUT_MS} budget, never extending the user's wait. A
 * 503/missing-grant failure is deterministic and is NOT retried. When the retries
 * are spent the user is finally told, through a non-blocking notice
 * ({@link UseUploadFlowOptions.onNotice}) that neither blocks navigation nor
 * becomes a batch error — because losing the second capture of two concurrent
 * sessions in silence, with no polling seam to recover it (ADR-0134 R2), was the
 * actual defect.
 */
async function completeSessionBestEffort(
  service: UploadsService,
  sessionId: string,
  timeoutMs: number = COMPLETE_SESSION_TIMEOUT_MS,
): Promise<CompleteSessionOutcome> {
  const deadline = Date.now() + timeoutMs
  let timer: ReturnType<typeof setTimeout> | undefined
  const ceiling = new Promise<CompleteSessionOutcome>((resolve) => {
    timer = setTimeout(() => resolve('quiet'), timeoutMs)
  })
  try {
    return await Promise.race([attemptCompletion(service, sessionId, deadline), ceiling])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

/**
 * What the caller should do about the completion call, which is only ever one of
 * two things (Security/code review S5):
 *
 *   - `'quiet'` — it succeeded, or it timed out, or it failed for a reason the
 *     user cannot act on (overwhelmingly: the BFF task role's missing
 *     `s3:GetObject`/`kms:Decrypt` grant, which fails in every deployed
 *     environment today). Say nothing. The bytes are in S3 and the flat staging
 *     view works, so there is no user-facing problem to report.
 *   - `'busy'` — the account's completion permit was held and stayed held across
 *     the bounded retries. The capture really did not happen, the user's own other
 *     session is why, and "it'll show up" would be untrue. This is the ONE case
 *     that earns a notice.
 */
type CompleteSessionOutcome = 'quiet' | 'busy'

/**
 * How many times a 429 is retried, on top of the first attempt.
 *
 * TWO, not "until the budget runs out". `Retry-After` is 1 s and the server's
 * measured worst case is ~1.2 s, so one retry recovers essentially every real
 * 429 and two covers a pathological queue — while the whole sequence stays an
 * order of magnitude inside {@link COMPLETE_SESSION_TIMEOUT_MS}. An unbounded
 * retry inside a fixed budget is just a spin loop that reports `busy` later.
 */
export const MAX_COMPLETE_RETRIES = 2

/** Ceiling on a single back-off, so a hostile/erroneous `Retry-After: 3600`
 * cannot park the user for the whole wait budget. */
const MAX_RETRY_BACKOFF_MS = 2_000
/** Used when the server sent no parseable `Retry-After`. Matches the BFF's `1`. */
const DEFAULT_RETRY_BACKOFF_MS = 1_000

/**
 * Attempt the completion, retrying ONLY a 429 and only within the wait budget.
 *
 * The 429 is special because it is the only failure here that is both
 * self-resolving and idempotently retryable: the server is telling us its
 * per-account permit is held right now (Gate 5 F1) and to come back shortly.
 * Retrying anything else — a 503, a missing grant, a malformed session — would
 * burn the user's wait budget re-provoking a deterministic failure.
 */
async function attemptCompletion(
  service: UploadsService,
  sessionId: string,
  deadline: number,
): Promise<CompleteSessionOutcome> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      await service.completeUploadSession(sessionId)
      return 'quiet'
    } catch (error: unknown) {
      // S5/N2 — the error is BOUND, not discarded by a bare `catch {}`. The server
      // still logs the real cause at error level, so this was never the only
      // record; what was missing is the client being able to ACT on it.
      const backoffMs = retryDelayForBusy(error, attempt, deadline)
      if (backoffMs === null) {
        return isBusy(error) ? 'busy' : 'quiet'
      }
      await new Promise<void>((resolve) => setTimeout(resolve, backoffMs))
    }
  }
}

/** True for the per-account completion gate's 429. */
function isBusy(error: unknown): boolean {
  return error instanceof CompleteUploadSessionError && error.status === 429
}

/**
 * The back-off before the next attempt, or `null` when there must not be one —
 * because the failure is not a 429, the retry budget is spent, or the back-off
 * would not fit inside the remaining wait budget. Never let a retry push the user
 * past the wait ceiling: that ceiling is a UX promise, not a suggestion.
 */
function retryDelayForBusy(error: unknown, attempt: number, deadline: number): number | null {
  if (!isBusy(error) || attempt >= MAX_COMPLETE_RETRIES) {
    return null
  }
  const requested = (error as CompleteUploadSessionError).retryAfterSeconds
  const backoffMs = Math.min(
    requested !== null ? requested * 1_000 : DEFAULT_RETRY_BACKOFF_MS,
    MAX_RETRY_BACKOFF_MS,
  )
  return Date.now() + backoffMs < deadline ? backoffMs : null
}

/**
 * The non-blocking notice shown when the account was too busy to capture the BOM
 * hierarchy even after the retries.
 *
 * NON-BLOCKING AND NOT AN ERROR, deliberately. The upload SUCCEEDED — the bytes
 * are in S3 and the flat staging view (ADR-0079) works — so this must not become
 * a batch-fatal error or stop the user reaching their staging page. It names the
 * remedy (re-upload later) because there is nothing to click: `StagingPage` has no
 * polling seam to pick the hierarchy up afterwards (ADR-0134 R2).
 */
/**
 * Not one file's bytes reached S3.
 *
 * UID-583 (ADR-0173 D5) — shown in TWO places now, which is why it is a shared
 * constant rather than an inline string: on the originating page (as
 * `batchError`, for a user who walks back to it) and on `StagingPage`, which
 * the flow has already navigated to by the time this is known. Two copies of
 * this sentence would drift, and the staging one is the copy the user actually
 * reads.
 *
 * "Try again" IS the correct remedy here, unlike the workbook case
 * ({@link SPREADSHEET_UNSUPPORTED_MESSAGE}) where retrying the identical files
 * fails identically: what failed is the browser→S3 transfer, not the file
 * selection.
 */
/**
 * Gate-5 C2 (scenario A) — a per-tab marker that THIS tab started an upload
 * for a session and never finished it.
 *
 * WHY IT EXISTS. ADR-0173 D1 puts the user on `/uploads/{sessionId}` for the
 * whole transfer, which for a large CAD batch is minutes. A reload in that
 * window kills the batch: the PUTs die, `completeUploadSession` is never
 * called, and — because the in-memory batch record dies with the page —
 * `batchFor` returns `null`, so nothing holds the triage read. The user lands
 * on a session with no BOM hierarchy and, with no polling seam (ADR-0134 R2),
 * that wrong answer is final and unexplained.
 *
 * The underlying gap PREDATES this story: on `main`, opening ANY session whose
 * completion call never ran shows the same hierarchy-less table (verified —
 * `main`'s `StagingPage` passes no gate to `useStaging`). What ADR-0173 D1
 * changed is how reachable it is, so this story is where it gets an
 * explanation instead of silence.
 *
 * `sessionStorage`, not `localStorage`: an interrupted upload is a fact about
 * THIS tab's page life and must not leak into another tab legitimately
 * watching the same session. Every access is wrapped — a privacy-mode browser
 * that throws on `sessionStorage` must degrade to the pre-existing (silent)
 * behaviour, never break the upload.
 */
const INTERRUPTED_UPLOAD_KEY_PREFIX = 'uid583.upload-in-flight.'

function markUploadInFlight(sessionId: string): void {
  try {
    sessionStorage.setItem(`${INTERRUPTED_UPLOAD_KEY_PREFIX}${sessionId}`, '1')
  } catch {
    // Storage unavailable/full — see this block's doc: degrade, never throw.
  }
}

function clearUploadInFlight(sessionId: string): void {
  try {
    sessionStorage.removeItem(`${INTERRUPTED_UPLOAD_KEY_PREFIX}${sessionId}`)
  } catch {
    // As above.
  }
}

/**
 * Did an upload for `sessionId` start in this tab and never reach a terminal
 * phase? Read by `StagingPage` ONLY when it has no live batch record — a live
 * record is always the better answer.
 */
export function wasUploadInterrupted(sessionId: string): boolean {
  try {
    return sessionStorage.getItem(`${INTERRUPTED_UPLOAD_KEY_PREFIX}${sessionId}`) !== null
  } catch {
    return false
  }
}

export const TRANSFER_FAILED_MESSAGE =
  'Your files were accepted but the upload didn’t finish. Check your connection and try again.'

export const BOM_CAPTURE_BUSY_NOTICE =
  'Your files uploaded, but we couldn’t read the assembly structure — another upload was ' +
  'finishing for your account. Everything is staged; re-upload the BOM spreadsheet if you ' +
  'need the hierarchy.'

/** Matches the design's own `isCsv` test (`Fictiv Platform.dc.html:6053`). */
const BOM_SPREADSHEET_EXT_RE = /\.(csv|tsv|txt)$/i

/**
 * UID-517 follow-up — the candidate "part name" `checkThumbnailReuse` asks the
 * Library about for one stageable file: the file's OWN base name, extension
 * stripped. This is the only identity signal available at the point in the
 * upload flow the pre-render check runs (BEFORE the presigned PUTs land and
 * BEFORE session completion parses the BOM, so the sheet's OWN stated part
 * names are not known yet) — a filename that happens to equal an existing
 * `custom` Library part's name is treated as the same part, mirroring the
 * exact-name convention `attachCrossSessionCustomThumbnails` already uses
 * server-side. Blank/extension-only names return `''`, which the caller
 * filters out before asking (an empty candidate can never match anything).
 */
export function candidateThumbnailReuseName(filename: string): string {
  const base = filename.split(/[/\\]/).pop() ?? filename
  const dot = base.lastIndexOf('.')
  return (dot > 0 ? base.slice(0, dot) : base).trim()
}

/**
 * A lightweight, CLIENT-SIDE row count for the "N parts parsed from …" toast
 * (design: `beginBomSession`'s `this.flash(...)`, `Fictiv Platform
 * .dc.html:6142`). This is deliberately NOT the same thing as the server's
 * authoritative BOM parse (which classifies Buy/Make, matches catalog SKUs,
 * etc., and is what actually drives `StagingPage`) — it's a same-instant,
 * best-effort count of non-blank data rows (total lines minus one header
 * row minus blanks), purely so the toast can show a number immediately,
 * the way the design's own fully-client-side (and equally naive) parse
 * does. If the server's real part count ever disagrees, the server's is
 * the one that matters everywhere else in the app (`partCount` on
 * `UploadSessionSummary`) — this toast is not a promise about that number.
 */
export function countBomCsvDataRows(text: string): number {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '')
  return Math.max(0, lines.length - 1)
}

/**
 * Gate 5 W5 — the hard bound on how long the user waits at the dropzone for the
 * capture call before being taken to their staging page anyway.
 *
 * WHY A BOUND AT ALL. ADR-0134 D2 accepts a blocking wait here (there is no
 * progress subsystem and no polling seam, so the hierarchy must exist before the
 * triage read), and the wait is bounded SERVER-side. But nothing bounded it
 * CLIENT-side, and at the time the UI showed no affordance during it: a stalled
 * connection, a proxy holding the socket, or the 429 back-off left the user in
 * front of a dropzone that had visibly finished uploading and simply did not
 * advance, with nothing to click and nothing to read.
 *
 * WHY 10s. Comfortably above the real worst case — the parse is measured at
 * 286-392 ms per completing 5 MB / 10,000-row file and 1144-1199 ms for the
 * four-attempt cap (Gate 5 F1), so a healthy capture is an order of magnitude
 * inside this — and short enough that a stall does not read as a hang.
 *
 * TIMING OUT IS NOT CANCELLING. The request is left in flight deliberately: the
 * endpoint is idempotent, its outcome is only ever read back through
 * `getStagingTriage`, and this function already discards the response. So a
 * late-completing capture still lands and the user still gets their hierarchy on
 * the NEXT triage read — they just are not made to wait for it.
 *
 * THIS WAS A FLOOR, NOT THE FIX — and UID-583 built the fix this note asked for.
 * The real affordance was named here as "an 'analyzing' state on `StagingPage`",
 * and ADR-0173 D1/D3 puts the user in front of exactly that for the whole wait.
 * This ceiling now bounds how long that panel can stay up before the triage read
 * is released anyway. ADR-0134 R2's polling seam is still open, which is why the
 * read is HELD rather than retried.
 */
export const COMPLETE_SESSION_TIMEOUT_MS = 10_000

/**
 * The seed→createSession→PUT→complete→navigate flow (UID-41) shared by any screen
 * that starts an upload the same way `UploadsPage` does (originally
 * `UploadsPage.tsx`'s own `uploadOne`/`handleFilesSelected`, extracted for
 * UID-125 so Home's upload entry point wires to the SAME seam instead of a
 * second copy of this logic).
 *
 * ADR-0135 D1 — SESSION-FIRST. `createSession` runs BEFORE any PUT and the browser
 * PUTs to the tickets it returns. It used to run LAST, after each file had
 * presigned itself via `POST /intake/uploads`, which meant the object key was
 * minted TWICE from two independent derivations: the browser PUT to one key and
 * the server persisted the other. Nothing reconciled them, so the byte-reading
 * seam (ADR-0134) head-objected a key nothing had been uploaded to and reported
 * `not-uploaded` for every file, permanently (ADR-0134 R5). The key now has
 * exactly ONE minter, which persists it in the same operation (D3a).
 *
 * UID-124: `uploadOne` performs the real browser→S3 byte transfer (`putFileToS3`,
 * replaying `ticket.headers` verbatim, streaming upload progress into
 * `updateFile`). The terminal `'uploaded'` status is only reached after a real 2xx
 * response from S3, so the state is honest: it means bytes actually landed in the
 * bucket. UID-583 — it is no longer DISPLAYED anywhere as a per-file label (the
 * "Ready" chip went with `UploadFileList`, ADR-0173); it is now read in aggregate,
 * as the `settledCount`/`pct` the staging screen's analysing panel shows.
 */
export function useUploadFlow(
  service: UploadsService = defaultUploadsService,
  options: UseUploadFlowOptions = {},
): UseUploadFlowResult {
  const navigate = useNavigate()
  const [files, setFiles] = useState<readonly UploadFileState[]>([])
  const [batchError, setBatchError] = useState<string | null>(null)
  /**
   * Gate-5 W5 — read through refs so `handleFilesSelected` can be a STABLE
   * `useCallback`. Same idiom as `useStaging`'s `serviceRef`. Without this the
   * handler is a fresh closure every render, which makes
   * `UploadFlowProvider`'s memoized context value re-identify on every XHR
   * progress event and re-render every consumer — and a memo that never holds
   * is worse than none, because it reads as protection that is not there.
   *
   * A ref rather than a dependency because both are effectively constant per
   * mount in every real call site (`defaultUploadsService`, and an inline
   * `onNotice` arrow) while being new OBJECTS each render — as dependencies
   * they would defeat the callback entirely.
   */
  const serviceRef = useRef(service)
  serviceRef.current = service
  const optionsRef = useRef(options)
  optionsRef.current = options
  /**
   * UID-583 (ADR-0173 D2) — one record per batch this hook has started,
   * KEYED BY `sessionId`.
   *
   * Held separately from `files` rather than as a field ON each row because
   * rows are seeded BEFORE `createSession` runs (ADR-0135 D1), so at seed
   * time there is no session id to stamp on them.
   *
   * A MAP, not a single "current batch" — Gate-5 finding C2 scenario B. With
   * one slot, starting a second batch overwrote the first, and every later
   * `setBatch` for the first was dropped by the `sessionId` guards. Reaching
   * that first session again (its resume card) then found `null` and released
   * the triage read on a session that was still completing. Batches are
   * per-tab and bounded by how many uploads a user starts in one page life,
   * so nothing needs evicting.
   */
  const [batches, setBatches] = useState<
    Readonly<
      Record<
        string,
        {
          readonly fileIds: readonly string[]
          /** Refused before any byte moved (workbook gate) — tracked
           * separately so they never count toward the transfer's total. */
          readonly refusedIds: readonly string[]
          readonly phase: UploadBatchPhase
          readonly batchError: string | null
        }
      >
    >
  >({})

  /** Patch one batch in place, by session id. A no-op if that session has no
   * record — the shape the old single-slot `prev.sessionId !== …` guards
   * expressed, now expressed by the key itself. */
  const patchBatch = useCallback(
    (sessionId: string, patch: Partial<{ phase: UploadBatchPhase; batchError: string | null }>) => {
      setBatches((prev) => {
        const current = prev[sessionId]
        if (current === undefined) return prev
        return { ...prev, [sessionId]: { ...current, ...patch } }
      })
    },
    [],
  )

  // The next three are `useCallback([])`/stable so `handleFilesSelected` below
  // can be stable too (Gate-5 W5) — each closes over nothing but a setState.
  const updateFile = useCallback((id: string, patch: Partial<UploadFileState>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)))
  }, [])

  /** ADR-0173 D3 — derived at read time from the SAME `files` rows the flow
   * already maintains, so the batch view can never drift from the per-file
   * truth (there is no second copy of `progress`/`status` to keep in sync). */
  const batchFor = useCallback(
    (sessionId: string): UploadBatchState | null => {
      const batch = batches[sessionId]
      if (batch === undefined) return null
      // `Set`, not `Array.includes` inside a `filter` — this runs on every
      // render, and renders happen on every XHR progress event (Gate-5 W5).
      const stageableIds = new Set(batch.fileIds)
      const refusedIds = new Set(batch.refusedIds)
      const rows = files.filter((file) => stageableIds.has(file.id))
      const failed = rows.filter((file) => file.status === 'failed')
      const settled = rows.filter((file) => file.status === 'uploaded' || file.status === 'failed')
      const describe = (file: UploadFileState, fallback: string) => ({
        id: file.id,
        fileName: file.fileName,
        error: file.error ?? fallback,
      })
      return {
        sessionId,
        phase: batch.phase,
        pct: byteWeightedProgress(rows),
        settledCount: settled.length,
        total: rows.length,
        failedFiles: failed.map((file) => describe(file, 'Upload failed')),
        // Gate-5 C1 — the workbook-gate rejects, which are NOT in `fileIds`
        // and so were previously reachable from no screen at all.
        refusedFiles: files
          .filter((file) => refusedIds.has(file.id))
          .map((file) => describe(file, SPREADSHEET_UNSUPPORTED_MESSAGE)),
        batchError: batch.batchError,
      }
    },
    [batches, files],
  )

  /**
   * `initial` is seeded by the CALLER (`handleFilesSelected`, in one batch
   * `setFiles` call) rather than pushed here — every row must be visible the
   * instant the batch starts, not materialise one-by-one as each parallel task
   * happens to reach this function (fix for the "each part loads individually"
   * defect, PR #362). This function only transitions an already-seeded row
   * through `uploading` -> terminal. ADR-0135 D7 keeps that contract exactly;
   * the only change is that the presigned `ticket` is now a PARAMETER instead of
   * something this function fetches for itself.
   */
  const uploadOne = useCallback(
    async function uploadOne(
      file: File,
      initial: UploadFileState,
      ticket: UploadSessionPutTicket,
    ): Promise<UploadFileState> {
      const { id } = initial
      updateFile(id, { status: 'uploading', progress: 0 })

      try {
        // ADR-0135 D1 — the ticket is HANDED IN by `createSession`, not fetched
        // here. This function no longer knows how a ticket comes to exist, which is
        // the whole point: there is one minter and it already persisted the key.
        // D4a — `ticket.maxBytes` is the SESSION-level `UploadSession.maxBytes`
        // composed on by the adapter, so this stays corroboration of a server
        // value, never a client-side duplicate of server policy (D5c).
        assertWithinMaxBytes(file, ticket)
        await putFileToS3(file, ticket, {
          onProgress: (progress) => updateFile(id, { progress }),
        })
        updateFile(id, { status: 'uploaded', progress: 100 })
        return { ...initial, status: 'uploaded', progress: 100 }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Upload failed'
        updateFile(id, { status: 'failed', error: message })
        return { ...initial, status: 'failed', error: message }
      }
    },
    [updateFile],
  )

  const handleFilesSelected = useCallback(
    async function handleFilesSelected(
      selected: readonly File[],
      workspaceId?: string,
      onSessionCreated?: (session: UploadSessionSummary) => Promise<void> | void,
    ): Promise<void> {
      setBatchError(null)
      // Kicked off NOW, in parallel with everything below (never awaited
      // until the very end, right before the notice/navigate point) — a
      // client-side row count for the "N parts parsed from …" toast. Only the
      // FIRST BOM-spreadsheet-shaped file in the batch, matching the design's
      // own `beginBomSession`/`ingestFiles` (one CSV per batch, `Fictiv
      // Platform.dc.html:6053`). `.catch()` swallows a read failure — this
      // toast is a nice-to-have, never a reason to fail or delay the upload.
      const bomCsvFile = selected.find((file) => BOM_SPREADSHEET_EXT_RE.test(file.name))
      // `typeof .text === 'function'` guards environments where `File.text()`
      // isn't available (some jsdom/test-double setups construct a plain
      // `{ name, size }` object rather than a real `File`) — same posture as
      // the `.catch()` below: this toast is a nice-to-have, never a reason to
      // throw. Raced against a timeout: `File.text()` reads a local blob and
      // should never actually hang, but this promise IS awaited below before
      // `navigate()` — an unbounded hang here would block the user from
      // reaching their already-successfully-uploaded session, which would be
      // far worse than just skipping this nice-to-have toast.
      const BOM_PARTS_NOTICE_TIMEOUT_MS = 1500
      const bomPartsNoticePromise =
        bomCsvFile && typeof bomCsvFile.text === 'function'
          ? Promise.race([
              bomCsvFile
                .text()
                .then((text) => {
                  const count = countBomCsvDataRows(text)
                  return `${count} part${count === 1 ? '' : 's'} parsed from “${bomCsvFile.name}”`
                })
                .catch(() => null),
              new Promise<null>((resolve) =>
                setTimeout(() => resolve(null), BOM_PARTS_NOTICE_TIMEOUT_MS),
              ),
            ])
          : null
      // Seed every row up front, in ONE state update, before any parallel
      // upload task starts — this is what makes the whole batch visible
      // immediately instead of rows popping in one-by-one as each task's own
      // async work happens to reach the point that used to push a row.
      //
      // ADR-0134 D7: a spreadsheet WORKBOOK (`.xlsx`/`.xls`/`.ods`/…) is seeded
      // straight to `'failed'` with the shared, actionable
      // `SPREADSHEET_UNSUPPORTED_MESSAGE` and never presigned. Workbooks are
      // unparseable by design (no OOXML reader — ADR-0086's security note), so
      // refusing them here is the HONEST answer; previously they were presigned,
      // 415ed server-side, and — because the batch is validated all-or-nothing —
      // collapsed into the useless "None of the selected files could be prepared
      // for upload." Per-FILE on purpose: nine `.step` files plus one `.xlsx`
      // uploads the nine and explains the one.
      const seeded: readonly UploadFileState[] = selected.map((file) =>
        isSpreadsheetWorkbook(file)
          ? {
              id: makeUploadFileId(),
              fileName: file.name,
              sizeBytes: file.size,
              status: 'failed',
              progress: 0,
              error: SPREADSHEET_UNSUPPORTED_MESSAGE,
            }
          : {
              id: makeUploadFileId(),
              fileName: file.name,
              sizeBytes: file.size,
              status: 'queued',
              progress: 0,
            },
      )
      setFiles((prev) => [...prev, ...seeded])

      // The files the server is actually asked to stage: everything the workbook
      // gate did not already refuse. Index-aligned pairs, kept together so the
      // request order, the ticket order and the row order are ONE ordering rather
      // than three that have to be trusted to agree (ADR-0135 D4).
      const stageable = selected.flatMap((file, index) => {
        const initial = seeded[index]
        if (!initial) throw new Error('unreachable: seeded is index-aligned with selected')
        // Already terminal (workbook-rejected, ADR-0134 D7) — never sent, never PUT.
        return initial.status === 'failed' ? [] : [{ file, initial }]
      })
      if (stageable.length === 0) {
        // Gate 5 W4 — the ONLY way to get here is that the workbook gate above
        // refused every file (it is the sole producer of a pre-terminal `'failed'`
        // seed), so the batch line says exactly that. It used to read "None of the
        // selected files could be prepared for upload. Try again." — false on both
        // counts: nothing was refused for a *preparation* reason, and retrying the
        // identical files fails identically, so "Try again" sent the user in a loop.
        // Reusing the shared message verbatim keeps the batch line and the per-row
        // lines from drifting apart, and it is the one with the real remedy (save
        // as CSV / paste the rows).
        setBatchError(SPREADSHEET_UNSUPPORTED_MESSAGE)
        return
      }

      // ADR-0135 D1 — STEP 1 of the network work, and the ONLY presign. This mints
      // each object key and persists it as `staged_file.object_key` in the same
      // operation, and hands back the presigned PUT the browser must use. Doing it
      // first is what makes the uploaded key and the persisted key the same key.
      let session: CreatedUploadSession
      try {
        const fileRequests = stageable.map(({ file }) => ({
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
        }))
        // Only pass a second argument when a workspace was actually selected —
        // `service.createSession(files, undefined)` and `service.createSession
        // (files)` are equivalent to any real implementation, but keeping the
        // call arity minimal matches every existing call site (e.g. Home,
        // which has no workspace selector) and its `toHaveBeenCalledWith`
        // assertions exactly.
        session = workspaceId
          ? await serviceRef.current.createSession(fileRequests, workspaceId)
          : await serviceRef.current.createSession(fileRequests)
      } catch (error) {
        // ADR-0135 D5 — the accepted regression. `createSession` validates
        // all-or-nothing, so a single off-allowlist or oversize file now fails the
        // WHOLE batch (415/413) rather than just its own row. The mitigation is
        // attribution, not a client-side mirror of the server allowlist: the
        // adapter surfaces the Problem `detail`, which is `${filename}: ${reason}`,
        // so the user learns WHICH file was refused (D5a). Two sources of truth for
        // one policy would be a worse and longer-lived defect (D5c). The real fix
        // is per-file rejection outcomes — residual R7, a breaking change.
        const message =
          error instanceof Error ? error.message : 'We couldn’t start a staging session.'
        setBatchError(message)
        // No bytes will ever be sent for these rows, so leaving them in `queued`
        // would be a permanent lie. Mark them failed with the same reason.
        //
        // Gate-5 W4 — HOOK STATE ONLY on this path, and that is now worth saying
        // out loud: `createSession` rejected, so no session exists, so D1's
        // navigation never fires and no batch record is ever created. There is
        // therefore no `batchFor` view over these rows and no screen renders
        // them — the user learns what happened from `batchError` above, on the
        // page they are still standing on (ADR-0173 D4). This write is kept
        // because `files` must not describe a queued transfer that will never
        // happen, not because anything currently displays it.
        setFiles((prev) =>
          prev.map((row) =>
            stageable.some(({ initial }) => initial.id === row.id)
              ? { ...row, status: 'failed', error: message }
              : row,
          ),
        )
        return
      }

      // UID-583 (ADR-0173 D1) — NAVIGATE NOW, before a single byte moves.
      //
      // This is the earliest point at which a navigation can name a real
      // session, and it is only available because ADR-0135 D1 made the flow
      // session-first: `createSession` has already minted and persisted every
      // object key and handed back the presigned PUTs. Everything below this
      // line therefore runs while the user is already looking at
      // `StagingPage`'s "Analyzing your files" panel, which is the point of
      // the story — the wait is not shortened, it is moved onto the screen
      // that is honest about it (D3).
      //
      // Registered BEFORE `navigate` so `StagingPage` can never mount and ask
      // `batchFor(sessionId)` before this flow has recorded the batch — a
      // `null` there means "not my upload" and would let the triage read fire
      // immediately, which is exactly the BOM-hierarchy-less screen ADR-0134
      // D2 exists to prevent.
      //
      // `state: { justUploaded: true }` is retained for UID-509's
      // minimum-display floor, which still covers the case where this flow's
      // batch record is absent (a resumed or bookmarked session).
      setBatches((prev) => ({
        ...prev,
        [session.sessionId]: {
          fileIds: stageable.map(({ initial }) => initial.id),
          // Gate-5 C1 — every row the workbook gate refused before any byte
          // moved. Tracked so the staging screen can name them; deliberately
          // NOT in `fileIds`, so they never count toward the transfer's
          // total/percentage (a file that was never sent must not report as
          // "uploaded" the instant the batch starts).
          refusedIds: seeded.filter((row) => row.status === 'failed').map((row) => row.id),
          phase: 'uploading',
          batchError: null,
        },
      }))
      // Gate-5 C2 — survives a reload, unlike the in-memory record above.
      markUploadInFlight(session.sessionId)
      navigate(`/uploads/${session.sessionId}`, { state: { justUploaded: true } })
      // UID-509 — fired alongside `navigate`, matching the design's own
      // sequencing (`Fictiv Platform.dc.html:6140`: `assignModal` is set in the
      // SAME state update as `this.go('staging')` + `runAnalysis()`). The modal
      // lives in `AssignToProjectProvider` above `<Routes>`, so it survives
      // this navigation. Not awaited — nothing downstream is gated on it.
      onSessionCreated?.(session)

      // ADR-0135 D7 — BINDING CONSTRAINT: every ticket was signed at t0, before the
      // first byte moved, and `PRESIGN_TTL_SECONDS` is 300. This is safe ONLY
      // because `Promise.all` starts every PUT immediately and S3 validates a
      // presigned signature at request RECEIPT, not completion — so a slow 100 MiB
      // body that started at t+2s still completes fine. Anyone who later adds a
      // concurrency limiter, a queue, or a sequential fallback here MUST either
      // re-presign per file at dequeue time or raise the TTL for this path: a
      // queued ticket can expire before its turn.
      const results = await Promise.all(
        stageable.map(({ file, initial }, index) => {
          // D4 — correlate BY INDEX, never by filename: filenames are not unique
          // within a batch (two dragged folders can each contain `bom.csv`) and
          // `UploadSessionFileRequest` carries no uniqueness constraint. The
          // alignment is a stated contract, pinned by an API test.
          const ticket = session.tickets[index]
          if (!ticket) {
            const message = 'The server did not return an upload ticket for this file.'
            updateFile(initial.id, { status: 'failed', error: message })
            return Promise.resolve<UploadFileState>({
              ...initial,
              status: 'failed',
              error: message,
            })
          }
          return uploadOne(file, initial, ticket)
        }),
      )

      // UID-517 follow-up — a PRE-RENDER library-reuse check, BULK (one request
      // for the whole batch, not one per file): before rendering anything,
      // ask whether any of this batch's files already name an existing
      // `custom` Library part with a real photo, so that file's render can be
      // skipped entirely instead of generating a brand-new thumbnail from
      // scratch for a part the account already has one for.
      //
      // FAIL-OPEN TO THE EXISTING PIPELINE, by construction: `reuseMatches`
      // defaults to `{}` (nothing matched) whenever the check itself is
      // skipped or fails, so the render loop below falls through to its
      // ORIGINAL, unconditional behavior for every file the check didn't
      // affirmatively match — never silently skipping a render because of a
      // check failure. Only an ACTUAL match suppresses a render.
      let reuseMatches: Readonly<Record<string, string>> = {}
      const reuseCandidates = stageable
        .map(({ file }) => candidateThumbnailReuseName(file.name))
        .filter((name) => name.length > 0)
      if (reuseCandidates.length > 0 && serviceRef.current.checkThumbnailReuse) {
        try {
          reuseMatches = await serviceRef.current.checkThumbnailReuse(
            session.sessionId,
            reuseCandidates,
          )
        } catch {
          // Best-effort: the check itself never blocks or fails the upload —
          // `reuseMatches` stays `{}` and every file renders as it always has.
        }
      }

      // UID-330 — fire thumbnail generation in the BACKGROUND (not awaited), AFTER
      // each file's bytes have landed in S3 (`generateThumbnail.ts`'s stated
      // calling convention), keyed to the SESSION's own server-minted file ids —
      // sourced from `session.tickets` (index-aligned with `stageable`/
      // `fileRequests`, ADR-0135 D4) rather than a separate result field — the
      // stable UUID the id-addressed thumbnail scheme uses across every surface,
      // so the PNG the display path (`GET .../thumbnails`) and later
      // staging/library rows derive is the same one. Best-effort: a failed PUT
      // already skips its row above (no ticket→no upload attempt); only files
      // that actually attempted the PUT get a thumbnail attempt.
      //
      // UID-517 follow-up — a file whose candidate name MATCHED the bulk check
      // above skips this render entirely: the Library already has a real photo
      // for that part, so rendering (and uploading) a brand-new one would be
      // pure waste. Every OTHER file — no match, or the check failed/was
      // skipped — renders EXACTLY as before this change (see `reuseMatches`'s
      // fail-open default above).
      stageable.forEach(({ file }, index) => {
        const id = session.tickets[index]?.uploadId
        if (!id) {
          return
        }
        const candidateName = candidateThumbnailReuseName(file.name)
        if (candidateName.length > 0 && Object.hasOwn(reuseMatches, candidateName)) {
          // Matched: a real Library photo already exists for this name. Skip
          // the CAD render entirely — the file itself still uploads/stages as
          // a normal drawing; only the thumbnail-GENERATION step is skipped.
          return
        }
        void generateAndUploadThumbnail(file, (sizeBytes) =>
          serviceRef.current.presignThumbnail(id, session.sessionId, sizeBytes),
        )
      })

      // ADR-0135 D6 — rows now exist for files whose PUT failed, and that is
      // correct: `completeUploadSession` reports them `not-uploaded`, which is
      // exactly what that status was for. Transport failures stay PER-FILE.
      if (!results.some((result) => result.status === 'uploaded')) {
        // Zero successes, leaving an empty session behind. Deliberately no rollback —
        // `DELETE /uploads/sessions/{sessionId}` (UID-172) already exists and
        // ADR-0108 owns the abandoned-draft lifecycle, so a client-driven un-stage
        // mutation would be net-new contract surface papering over a transport error.
        // Gate 5 W4 — these files WERE prepared: the server accepted them, minted
        // and persisted a key for each, and handed back a presigned PUT. What failed
        // was the browser→S3 byte transfer. "could be prepared for upload" pointed
        // the user at the wrong thing (their file selection) instead of the right one
        // (the transfer), while "Try again" — unlike the workbook case above — IS the
        // correct remedy here, so it stays.
        //
        // UID-583 (ADR-0173 D5) — the user is ALREADY on the staging page by now
        // (D1 navigated before the PUTs), so unlike every pre-UID-583 version of
        // this branch there is no dropzone in front of them to read `batchError`.
        // The load-bearing part is the `'failed'` phase: without it `StagingPage`
        // would hold its analysing panel forever waiting for a batch that has
        // already given up.
        //
        // Gate-5 C4 — an earlier version of this comment claimed `setBatchError`
        // was "what the originating page shows if the user walks back to it".
        // That was FALSE: both entry points call `clearBatchError()` in a mount
        // effect (deliberately — the provider outlives them, so a stale message
        // would otherwise reappear with no upload in flight), which clears this
        // before paint. It is kept because it is the honest hook state for a
        // batch that failed, and it is what a caller holding the flow directly
        // reads; it is NOT a user-facing surface on this path.
        setBatchError(TRANSFER_FAILED_MESSAGE)
        patchBatch(session.sessionId, { phase: 'failed', batchError: TRANSFER_FAILED_MESSAGE })
        // Terminal: this batch is not "interrupted", it finished and failed,
        // and the `'failed'` phase is what says so on screen.
        clearUploadInFlight(session.sessionId)
        return
      }
      // ADR-0173 D2 — every PUT has reached a terminal state; the bounded
      // BOM-hierarchy capture is what remains.
      patchBatch(session.sessionId, { phase: 'completing' })
      // S5 — navigation is NOT conditional on the outcome. The upload succeeded; the
      // notice rides alongside it.
      const outcome = await completeSessionBestEffort(serviceRef.current, session.sessionId)
      if (outcome === 'busy') {
        // UID-583 — this used to run BEFORE `navigate`, because the page owning
        // this hook was still mounted and a notice raised after the navigation
        // would have been announced by an unmounted component. That reasoning is
        // now spent: ADR-0173 D1 navigates before the PUTs, so by here the
        // originating page is ALREADY unmounted. The notice still lands because
        // `onNotice` is the toast host's own `showToast`, and `ToastProvider`
        // lives above `<Routes>` — the callback closes over a provider that is
        // still mounted, which is precisely why this seam was made a callback
        // rather than hook state (see `UseUploadFlowOptions.onNotice`).
        optionsRef.current.onNotice?.(BOM_CAPTURE_BUSY_NOTICE)
      }
      // Same reasoning as the busy notice above. By now the read kicked off at
      // the top of this function has long since resolved (uploading + completing
      // the session takes far longer than reading one small text file), so this
      // `await` costs nothing in practice.
      const bomPartsNotice = await bomPartsNoticePromise
      if (bomPartsNotice) {
        optionsRef.current.onNotice?.(bomPartsNotice, 'top')
      }
      // UID-583 (ADR-0173 D3) — the flow is done with this session, so the
      // triage read may now happen. Reached even when the capture call timed
      // out or failed: `completeSessionBestEffort` never throws, and a late
      // capture still lands and is picked up by the next triage read (ADR-0134's
      // own reasoning for leaving the request in flight). Holding the analysing
      // panel open past this point would be waiting for something that is not
      // coming.
      //
      // The `navigate` + `onSessionCreated` that used to be HERE moved to
      // immediately after `createSession` (D1) — see that block for why.
      patchBatch(session.sessionId, { phase: 'done' })
      clearUploadInFlight(session.sessionId)
    },
    [navigate, patchBatch, updateFile, uploadOne],
  )

  const clearBatchError = useCallback(() => setBatchError(null), [])

  return { files, batchError, handleFilesSelected, batchFor, clearBatchError }
}
